import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import PageHeader from '../../components/PageHeader';
import Button from '../../components/ui/Button';
import { Boxes, GitBranch, X, ArrowRight, SprayCan } from 'lucide-react';
import LotSprayHistory from '../../components/nursery/LotSprayHistory';
import { useModuleLicense } from '../../hooks/useModuleLicense';
import { sprayLinkFor } from '../../utils/nurserySprayTargets';
import nurseryService from '../../services/nursery';
import { downloadBlob } from '../../utils/downloadBlob';
import { useOrgTimezone, formatLocalDateInTz } from '../../utils/timezone';
import { slotWhere, compareSlots } from '../../utils/nurseryRowSlots';
import type {
  NurseryLot, NurseryLotLineage, NurseryLocation, MovementEventType, ApplyMovementPayload,
} from '../../types/nursery';

const EVENTS: { value: MovementEventType; label: string; needsDestination?: boolean; terminal?: boolean; grade?: boolean }[] = [
  { value: 'transplant', label: 'Transplant (move to new location)', needsDestination: true },
  { value: 'pot_up', label: 'Pot up', needsDestination: true },
  { value: 'graft', label: 'Graft', needsDestination: true },
  { value: 'bud', label: 'Bud', needsDestination: true },
  { value: 'split', label: 'Split', needsDestination: true },
  { value: 'grade', label: 'Grade (record counts)', grade: true },
  { value: 'dig', label: 'Dig (lift bareroot)', terminal: true },
  { value: 'ship', label: 'Ship', terminal: true },
  { value: 'sell', label: 'Sell', terminal: true },
  { value: 'cull', label: 'Cull', terminal: true },
  { value: 'adjust', label: 'Adjust count', grade: false },
];

const todayStr = () => new Date().toISOString().slice(0, 10);

function LotDetail() {
  const { id } = useParams<{ id: string }>();
  const lotId = Number(id);
  const orgTz = useOrgTimezone();
  const navigate = useNavigate();
  // Nursery spraying is the spray module's: planning one takes a spray
  // creator or admin seat, and opening one any spray seat.
  const { hasAccess: canPlanSpray } = useModuleLicense('spray', ['creator', 'admin']);
  const { hasAccess: canOpenSprays } = useModuleLicense('spray', ['operator', 'creator', 'admin']);

  const [lot, setLot] = useState<NurseryLot | null>(null);
  const [lineage, setLineage] = useState<NurseryLotLineage | null>(null);
  const [locations, setLocations] = useState<NurseryLocation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [showMove, setShowMove] = useState(false);
  const [mvEvent, setMvEvent] = useState<MovementEventType>('transplant');
  const [mvQty, setMvQty] = useState<number | ''>('');
  const [mvDate, setMvDate] = useState(todayStr());
  const [mvLocation, setMvLocation] = useState<number | ''>('');
  const [mvNotes, setMvNotes] = useState('');
  const [mvSource, setMvSource] = useState('');
  const [mvFirst, setMvFirst] = useState<number | ''>('');
  const [mvSecond, setMvSecond] = useState<number | ''>('');
  const [mvCull, setMvCull] = useState<number | ''>('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [lotRes, lineageRes, locRes] = await Promise.all([
        nurseryService.getLot(lotId),
        nurseryService.getLotLineage(lotId),
        nurseryService.getLocations({ active: true }),
      ]);
      if (lotRes.success) setLot(lotRes.data || null);
      else setError('Lot not found');
      if (lineageRes.success) setLineage(lineageRes.data || null);
      if (locRes.success) setLocations(locRes.data || []);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load lot');
    } finally {
      setLoading(false);
    }
  }, [lotId]);

  useEffect(() => { load(); }, [load]);

  const eventMeta = EVENTS.find(e => e.value === mvEvent)!;
  // Walk order, so the two sides of a track sit together, and never the place
  // the lot already stands in.
  const destinations = locations
    .filter(l => l.id !== lot?.location_id)
    .sort((a, b) => compareSlots({ ...a, location_name: a.name }, { ...b, location_name: b.name }));

  const submitMove = async () => {
    if (mvQty === '' && mvEvent !== 'grade') { setSaveError('Quantity is required'); return; }
    // A transplant that names no new place splits the lot where it stands,
    // which reads as trees added with none taken away.
    if (mvEvent === 'transplant' && (mvLocation === '' || mvLocation === lot?.location_id)) {
      setSaveError('Choose where the trees are going'); return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      const payload: ApplyMovementPayload = {
        event_type: mvEvent,
        from_lot_id: lotId,
        quantity: mvEvent === 'grade' ? 0 : Number(mvQty || 0),
        movement_date: mvDate,
        notes: mvNotes || null,
      };
      if (eventMeta.needsDestination && mvLocation) payload.to_location_id = Number(mvLocation);
      if (mvSource) payload.source_supplier = mvSource;
      if (mvEvent === 'grade') {
        payload.grading = {
          first_grade_count: mvFirst === '' ? 0 : Number(mvFirst),
          second_grade_count: mvSecond === '' ? 0 : Number(mvSecond),
          cull_count: mvCull === '' ? 0 : Number(mvCull),
          count_before: lot?.current_count,
        };
      }
      const res = await nurseryService.applyMovement(payload);
      if (res.success) {
        setShowMove(false);
        resetMove();
        load();
      } else {
        setSaveError((res as { error?: { message?: string } }).error?.message || 'Failed to record movement');
      }
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : 'Unexpected error');
    } finally {
      setSaving(false);
    }
  };

  const resetMove = () => {
    setMvEvent('transplant'); setMvQty(''); setMvDate(todayStr()); setMvLocation('');
    setMvNotes(''); setMvSource(''); setMvFirst(''); setMvSecond(''); setMvCull(''); setSaveError(null);
  };

  if (loading) return <div className="max-w-4xl mx-auto px-4 py-6"><div className="h-40 bg-neutral-100 rounded-lg animate-pulse" /></div>;
  if (error || !lot) return <div className="max-w-4xl mx-auto px-4 py-6"><div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700">{error || 'Lot not found'}</div></div>;

  return (
    <>
      <PageHeader
        title={`${lot.cultivar_name}${lot.rootstock_name ? ' / ' + lot.rootstock_name : ''}`}
        subtitle={`${lot.tree_type_code} · ${lot.current_stage} · ${lot.current_count.toLocaleString()} trees`}
        icon={<Boxes className="h-6 w-6 text-white" />}
        actionButtons={
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" onClick={async () => {
              try {
                downloadBlob(await nurseryService.getLotLabelsPdf([lot.id]), `lot-label-${lot.lot_code || lot.id}.pdf`);
              } catch { /* the download simply does not happen; nothing else depends on it */ }
            }}>
              Print label
            </Button>
            {canPlanSpray && lot.location_id != null && lot.status === 'active' && (
              <Button variant="outline" size="sm" onClick={() => {
                const here = locations.find(l => l.id === lot.location_id);
                navigate(sprayLinkFor({
                  farmId: lot.farm_id ?? here?.farm_id ?? null,
                  locationIds: [lot.location_id as number],
                }));
              }}>
                <SprayCan className="h-4 w-4" /> Spray
              </Button>
            )}
            <Button variant="primary" size="sm" onClick={() => setShowMove(true)} disabled={lot.status !== 'active'}>
              Record movement
            </Button>
          </div>
        }
      />
      <div className="max-w-4xl mx-auto px-4 py-6">
        {/* Facts */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          {[
            ['Status', lot.status],
            ['Stage', lot.current_stage],
            ['Location', lot.location_name || '—'],
            ['Ready', lot.ready_date ? formatLocalDateInTz(lot.ready_date, orgTz) : '—'],
            ['Count', lot.current_count.toLocaleString()],
            ['Initial', lot.initial_count.toLocaleString()],
            ['Block', lot.block_name || '—'],
            ['Farm', lot.farm_name || '—'],
          ].map(([k, v]) => (
            <div key={k} className="bg-white border border-neutral-200 rounded-lg px-3 py-2">
              <div className="text-xs text-neutral-400">{k}</div>
              <div className="text-sm font-medium text-neutral-800">{v}</div>
            </div>
          ))}
        </div>

        <div className="mb-6">
          <LotSprayHistory lotId={lot.id} canOpenSprays={canOpenSprays} />
        </div>

        {/* Lineage */}
        <div className="bg-white border border-neutral-200 rounded-xl p-5">
          <div className="flex items-center gap-2 mb-4">
            <GitBranch className="h-5 w-5 text-primary-600" />
            <h3 className="text-sm font-semibold text-neutral-800">Lineage &amp; movements</h3>
          </div>
          {!lineage || lineage.movements.length === 0 ? (
            <p className="text-sm text-neutral-400">No movements recorded yet.</p>
          ) : (
            <ol className="relative border-l border-neutral-200 ml-2 space-y-4">
              {lineage.movements.map(m => {
                const fromLot = lineage.lots.find(l => l.id === m.from_lot_id);
                const toLot = lineage.lots.find(l => l.id === m.to_lot_id);
                return (
                  <li key={m.id} className="ml-4">
                    <span className="absolute -left-1.5 w-3 h-3 rounded-full bg-primary-500 border-2 border-white" />
                    <div className="flex items-center gap-2 text-sm">
                      <span className="font-medium text-neutral-800 capitalize">{m.event_type.replace('_', ' ')}</span>
                      <span className="text-neutral-400">{formatLocalDateInTz(m.movement_date, orgTz)}</span>
                      {m.quantity > 0 && <span className="text-neutral-600">· {m.quantity.toLocaleString()}</span>}
                    </div>
                    <div className="flex items-center gap-2 text-xs text-neutral-500 mt-0.5">
                      {fromLot ? <span>Lot #{fromLot.id} {fromLot.cultivar_name}</span> : <span>{m.source_supplier || 'External'}</span>}
                      <ArrowRight className="h-3 w-3" />
                      {toLot ? <span>Lot #{toLot.id}{toLot.location_name ? ` · ${toLot.location_name}` : ''}</span> : <span>—</span>}
                    </div>
                    {m.notes && <div className="text-xs text-neutral-400 mt-0.5">{m.notes}</div>}
                  </li>
                );
              })}
            </ol>
          )}
        </div>

        {/* Movement modal */}
        {showMove && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-base font-semibold text-neutral-800">Record movement</h3>
                <button onClick={() => { setShowMove(false); resetMove(); }}><X className="h-5 w-5 text-neutral-400 hover:text-neutral-600" /></button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">Event</label>
                  <select value={mvEvent} onChange={e => setMvEvent(e.target.value as MovementEventType)}
                    className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm">
                    {EVENTS.map(e => <option key={e.value} value={e.value}>{e.label}</option>)}
                  </select>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  {mvEvent !== 'grade' && (
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 mb-1">
                        {mvEvent === 'adjust' ? 'New count' : 'Quantity'}
                      </label>
                      <input type="number" min="0" value={mvQty}
                        onChange={e => setMvQty(e.target.value === '' ? '' : parseInt(e.target.value, 10))}
                        className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm" />
                    </div>
                  )}
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-1">Date</label>
                    <input type="date" value={mvDate} onChange={e => setMvDate(e.target.value)}
                      className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm" />
                  </div>
                </div>

                {eventMeta.needsDestination && (
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-1">Destination location</label>
                    <select value={mvLocation} onChange={e => setMvLocation(e.target.value ? Number(e.target.value) : '')}
                      className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm">
                      <option value="">{mvEvent === 'transplant' ? 'Choose a place…' : 'Where it stands now'}</option>
                      {destinations.map(l => (
                        <option key={l.id} value={l.id}>
                          {l.location_type === 'nursery_row' && l.row_number != null
                            ? slotWhere({ ...l, location_name: l.name })
                            : [l.block_name, l.name].filter(Boolean).join(' · ')}
                        </option>
                      ))}
                    </select>
                    <p className="text-xs text-neutral-400 mt-1">
                      Takes {mvQty || 0} from this lot{lot.location_name ? ` at ${lot.location_name}` : ''}, leaving {Math.max(lot.current_count - Number(mvQty || 0), 0).toLocaleString()}, and starts a new lot at the destination.
                    </p>
                  </div>
                )}

                {(mvEvent === 'graft' || mvEvent === 'bud') && (
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-1">Graft/bud wood source</label>
                    <input value={mvSource} onChange={e => setMvSource(e.target.value)}
                      className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm" placeholder="supplier or donor reference" />
                  </div>
                )}

                {mvEvent === 'grade' && (
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 mb-1">1st grade</label>
                      <input type="number" min="0" value={mvFirst} onChange={e => setMvFirst(e.target.value === '' ? '' : parseInt(e.target.value, 10))}
                        className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 mb-1">2nd grade</label>
                      <input type="number" min="0" value={mvSecond} onChange={e => setMvSecond(e.target.value === '' ? '' : parseInt(e.target.value, 10))}
                        className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 mb-1">Cull</label>
                      <input type="number" min="0" value={mvCull} onChange={e => setMvCull(e.target.value === '' ? '' : parseInt(e.target.value, 10))}
                        className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm" />
                    </div>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">Notes</label>
                  <input value={mvNotes} onChange={e => setMvNotes(e.target.value)}
                    className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm" />
                </div>

                {saveError && <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-sm text-red-700">{saveError}</div>}
              </div>

              <div className="flex justify-end gap-2 mt-6">
                <Button variant="outline" size="sm" onClick={() => { setShowMove(false); resetMove(); }}>Cancel</Button>
                <Button variant="primary" size="sm" onClick={submitMove} disabled={saving}>
                  {saving ? 'Recording…' : 'Record'}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

export default LotDetail;
